import { RightOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { DateTime } from 'luxon';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Redirect } from 'react-router-dom';

import {
  AuthActions,
  PendingDocumentsActions,
  OnboardingActions,
} from '@portal/actions';
import useReduxState from '@portal/hooks/useReduxState';
import {
  DocumentPendingStatus,
  DocumentType,
} from '@portal/models/enumerators/onboarding';
import * as MessageService from '@portal/services/message';
import { translate } from '@portal/services/i18n';
import usePrevious from '@portal/hooks/usePrevious';
import { getPendingDocuments } from '@portal/actions/user';
import PendingDocumentsUploadModal from '@portal/components/PendingDocumentsUploadModal/PendingDocumentsUploadModal';
import PendingDocumentsModal from '@portal/components/PendingDocumentsModal/PendingDocumentsModal';
import { addMask, maskCpf, unmaskField } from '@portal/services/masks';
import { TAB_KEY } from '@portal/enum/onboardingType';

import { onboarding } from '@portal/config/app.json';
import IdwallUploadModal from '@portal/components/IdwallUploadModal/IdwallUploadModal';
import { AccountTaxIdentifierType } from '@portal/models/enumerators';
import AdvancedPanelContentHeader from '@portal/components/AdvancedPanelContentHeader/AdvancedPanelContentHeader';
import AdvancedCard from '@portal/components/AdvancedCard/AdvancedCard';
import { useNavigation } from '@portal/hooks/useNavigation';

const PendingDocuments = () => {
  const initialAddress: models.Address = {
    addressNumber: null,
    city: null,
    complement: null,
    neighborhood: null,
    state: null,
    street: null,
    zipCode: null,
  };

  const {
    user: {
      me: { account },
    },
    theme: { currentTheme },
  } = useReduxState();

  const dispatch = useDispatch();
  const navigate = useNavigation();

  const {
    user: {
      pendingDocuments,
      pendingDocumentsUploadSuccess,
      pendingDocumentsError,
      me,
    },
    auth: { authToken },
    onboarding: { settings, getAddress },
  } = useReduxState();

  const [currentDocument, setCurrentDocument] = useState<
    models.PendingDocument | undefined
  >();
  const [documentType, setDocumentType] = useState<
    DocumentType.CNH | DocumentType.RG | DocumentType.SELFIE
  >(DocumentType.CNH);
  const [currentFile, setCurrentFile] = useState<File | null>(null);
  const [currentSecondFile, setCurrentSecondFile] = useState<File | null>(null);
  const [currentValue, setCurrentValue] = useState<string | null>(null);
  const [currentOption, setCurrentOption] = useState<number | null>(null);
  const [addressData, setAddressData] =
    useState<models.Address>(initialAddress);

  const prevPendingDocuments = usePrevious(pendingDocuments);

  useEffect(() => {
    if (
      prevPendingDocuments?.length === pendingDocuments?.length ||
      !!prevPendingDocuments
    ) {
      return;
    }
    if (pendingDocuments && pendingDocuments.length > 0 && authToken) {
      MessageService.warn(
        'PAGES.PENDING_DOCUMENTS.YOU_HAVE_PENDING_DOCUMENTS',
        8
      );
    }
    dispatch(getPendingDocuments());
  }, [authToken, dispatch, pendingDocuments, prevPendingDocuments]);

  useEffect(() => {
    if (pendingDocumentsError) {
      MessageService.error(pendingDocumentsError.message, {
        originalError: pendingDocumentsError,
      });
      setCurrentFile(null);
      setCurrentValue(null);
      setCurrentSecondFile(null);
      setCurrentDocument(undefined);
    }
  }, [pendingDocumentsError]);

  useEffect(() => {
    if (pendingDocumentsUploadSuccess) {
      MessageService.success('PAGES.PENDING_DOCUMENTS.SUCCESSFULLY_UPLOADED');
      setCurrentFile(null);
      setCurrentValue(null);
      setCurrentSecondFile(null);
      setCurrentDocument(undefined);

      dispatch(getPendingDocuments());
    }
  }, [dispatch, pendingDocumentsUploadSuccess]);

  useEffect(() => {
    if (!settings) {
      dispatch(OnboardingActions.onboardingSettings());
    }
  }, [dispatch, settings]);

  useEffect(() => {
    const cleanZipCode = unmaskField(addressData.zipCode);
    if (cleanZipCode && cleanZipCode.length === 8) {
      dispatch(OnboardingActions.getAddress(cleanZipCode));
    }
  }, [addressData.zipCode, dispatch]);

  useEffect(() => {
    if (getAddress) {
      setAddressData((prev) => ({
        ...prev,
        ...getAddress,
        zipCode: prev.zipCode,
      }));
    }
  }, [getAddress]);

  if (!pendingDocuments || !authToken) {
    return <Redirect to="/" />;
  }

  if (pendingDocuments.length <= 0 && authToken) {
    navigate('/abertura-de-conta', {
      state: {
        tab: TAB_KEY.FINAL,
        accountType: me.account.type,
      },
    });
  }

  const handleUpload = (ocrToken?: string) => {
    const type =
      currentDocument?.type === DocumentType.PHOTO_DOCUMENT
        ? documentType
        : currentDocument?.type;

    if (
      currentDocument?.type === DocumentType.ID_WALL_SELFIE_DOCS &&
      onboarding.useIdwall &&
      ocrToken
    ) {
      dispatch(
        PendingDocumentsActions.saveAndUploadIdwall({
          id: currentDocument?.id ?? '',
          type: DocumentType.ID_WALL_SELFIE_DOCS,
          ocrToken,
          origin: 1,
          pj: account.type === AccountTaxIdentifierType.CORPORATE,
        })
      );

      return;
    }

    dispatch(
      PendingDocumentsActions.saveAndUpload({
        id: currentDocument?.id ?? '',
        type: type ?? DocumentType.SELFIE,
        file:
          currentDocument?.type === DocumentType.SELFIE && onboarding.useIdwall
            ? null
            : currentFile,
        secondFile: currentSecondFile,
        mimeType: currentFile?.type,
      })
    );
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    if (!currentDocument) {
      return;
    }

    switch (currentDocument.type) {
      case DocumentType.CPF:
        setCurrentValue(maskCpf(value));
        break;
      case DocumentType.BORN_AT:
        setCurrentValue(
          addMask(value, translate('APPLICATION.DEFAULT_DATE_FORMAT.MASK'))
        );
        break;
      default:
        setCurrentValue(value);
        break;
    }
  };

  const handleAddressChange = (key: string, value: string) => {
    setAddressData({
      ...addressData,
      [key]: value,
    });
  };

  const handleSubmit = (
    shouldSendOnlyNumber: boolean,
    shouldTakeIdFromValue = false
  ) => {
    if (!currentDocument || (!currentValue && !currentOption && !addressData)) {
      return;
    }
    let value = shouldSendOnlyNumber ? unmaskField(currentValue) : currentValue;

    if (shouldTakeIdFromValue && currentOption) {
      value = currentOption.toString();
    }

    if (currentDocument.type === DocumentType.BORN_AT && currentValue) {
      value = DateTime.fromFormat(
        currentValue,
        translate('APPLICATION.DEFAULT_DATE_FORMAT.LUXON')
      ).toISODate();
    }

    if (value) {
      dispatch(
        PendingDocumentsActions.save({
          id: currentDocument.id,
          type: currentDocument.type,
          value,
        })
      );
    } else if (addressData) {
      dispatch(
        PendingDocumentsActions.save({
          id: currentDocument.id,
          type: DocumentType.ADDRESS,
          address: {
            ...addressData,
            zipCode: unmaskField(addressData.zipCode),
          },
        })
      );
    }
  };

  const handlePress = (pendingDocument: models.PendingDocument) => {
    if (
      pendingDocument.isOK ||
      pendingDocument.status === DocumentPendingStatus.NO_PENDING
    ) {
      return;
    }

    // Check if this is ID_WALL_SELFIE_DOCS type
    if (pendingDocument.type === DocumentType.ID_WALL_SELFIE_DOCS) {
      // Check if there are other pending documents that haven't been submitted yet
      const hasOtherPendingDocuments = pendingDocuments.some(
        (doc) =>
          doc.id !== pendingDocument.id &&
          !doc.isOK &&
          doc.status !== DocumentPendingStatus.NO_PENDING
      );

      if (hasOtherPendingDocuments) {
        MessageService.warn('PAGES.PENDING_DOCUMENTS.ID_WALL_MUST_BE_LAST', 8);
        return;
      }
    }

    setCurrentDocument(pendingDocument);
  };

  const handleLogout = () => {
    dispatch(AuthActions.logout());
  };

  const renderModal = () => {
    if (!currentDocument) {
      return;
    }
    const { type, name } = currentDocument;

    if (type === DocumentType.ID_WALL_SELFIE_DOCS && onboarding.useIdwall) {
      return (
        <IdwallUploadModal
          isOpen={!!currentDocument}
          onCancel={() => setCurrentDocument(undefined)}
          onComplete={(token) => {
            handleUpload(token);
          }}
          documentType={type}
        />
      );
    }

    const chooseDocument =
      type === DocumentType.PHOTO_DOCUMENT &&
      onboarding.requireTwodriverLicensePictures;

    const uniquePhoto =
      type === DocumentType.RG_BACK ||
      type === DocumentType.RG_FRONT ||
      type === DocumentType.CNH_FRONT ||
      type === DocumentType.CNH_BACK ||
      type === DocumentType.SELFIE ||
      type === DocumentType.POWER_OF_ATTORNEY ||
      type === DocumentType.ADDRESS_PROOF ||
      type === DocumentType.CNPJ_PROOF ||
      type === DocumentType.PHOTO_DOCUMENT ||
      type === DocumentType.CUSTOM ||
      type === DocumentType.SOCIAL_CONTRACT ||
      type === DocumentType.COMPANY_STATUTE ||
      type === DocumentType.DECLARATION_OF_BENEFICIARIES ||
      type === DocumentType.PROOF_OF_INVOICING;

    const specificDoublePhoto =
      type === DocumentType.CNH || type === DocumentType.RG;

    const shouldSendOnlyNumber =
      type === DocumentType.CPF ||
      type === DocumentType.ESTABLISH_FORM ||
      type === DocumentType.FINANCIAL_STATISTIC;

    const isText =
      type === DocumentType.BORN_AT || type === DocumentType.MOTHER_NAME;

    const isSelect = type === DocumentType.BUSINESS_LINE;

    const isAddress = type === DocumentType.ADDRESS;

    if (specificDoublePhoto) {
      return (
        <PendingDocumentsUploadModal
          documentType={type}
          onFileChange={(e) =>
            setCurrentFile(e.target.files ? e.target.files[0] : null)
          }
          onSecondFileChange={(e) =>
            setCurrentSecondFile(e.target.files ? e.target.files[0] : null)
          }
          modal={!!currentDocument}
          onUpload={handleUpload}
          onCancel={() => {
            setCurrentFile(null);
            setCurrentSecondFile(null);
            setCurrentDocument(undefined);
          }}
          file={currentFile}
          secondFile={currentSecondFile}
        />
      );
    }
    if (chooseDocument) {
      return (
        <PendingDocumentsUploadModal
          chooseDocument
          documentType={DocumentType.PHOTO_DOCUMENT}
          onFileChange={(e) =>
            setCurrentFile(e.target.files ? e.target.files[0] : null)
          }
          onSecondFileChange={(e) =>
            setCurrentSecondFile(e.target.files ? e.target.files[0] : null)
          }
          modal={!!currentDocument}
          setDocumentType={setDocumentType}
          photoDocumentType={documentType as DocumentType.CNH}
          onUpload={handleUpload}
          onCancel={() => {
            setCurrentFile(null);
            setCurrentSecondFile(null);
            setCurrentDocument(undefined);
          }}
          file={currentFile}
          secondFile={currentSecondFile}
        />
      );
    }

    if (uniquePhoto) {
      return (
        <PendingDocumentsUploadModal
          uniquePhoto
          pendingDocumentName={name}
          documentType={type}
          onFileChange={(e) =>
            setCurrentFile(e.target.files ? e.target.files[0] : null)
          }
          modal={!!currentDocument}
          onUpload={handleUpload}
          onCancel={() => {
            setCurrentFile(null);
            setCurrentSecondFile(null);
            setCurrentDocument(undefined);
          }}
          file={currentFile}
        />
      );
    }
    if (isText || shouldSendOnlyNumber) {
      return (
        <PendingDocumentsModal
          documentType={type}
          onSubmit={() => handleSubmit(shouldSendOnlyNumber)}
          onCancel={() => {
            setCurrentValue(null);
            setCurrentDocument(undefined);
          }}
          onChange={handleChange}
          value={currentValue ?? ''}
          visible={!!currentDocument}
        />
      );
    }
    if (isSelect) {
      return (
        <PendingDocumentsModal
          isSelect
          documentType={type as DocumentType.BUSINESS_LINE}
          onSubmit={() => handleSubmit(shouldSendOnlyNumber, isSelect)}
          onCancel={() => {
            setCurrentValue(null);
            setCurrentDocument(undefined);
          }}
          onChange={(value) => setCurrentOption(value)}
          value={currentValue ?? ''}
          visible={!!currentDocument}
          options={settings?.businessLine ?? []}
        />
      );
    }
    if (isAddress) {
      return (
        <PendingDocumentsModal
          isAddress
          addressData={addressData}
          onChange={handleAddressChange}
          documentType={type as DocumentType.ADDRESS}
          onSubmit={() => handleSubmit(shouldSendOnlyNumber, isSelect)}
          onCancel={() => {
            setAddressData(initialAddress);
            setCurrentDocument(undefined);
          }}
          visible={!!currentDocument}
        />
      );
    }
  };

  const handleDocumentName = (pendingDocument: models.PendingDocument) => {
    if (pendingDocument.type === DocumentType.CUSTOM) {
      return pendingDocument.name;
    }

    if (pendingDocument.representorId) {
      return translate(
        `PAGES.PENDING_DOCUMENTS.DOCUMENTS.${
          DocumentType[pendingDocument.type]
        }_REPRESENTATIVE`
      );
    }

    return translate(
      `PAGES.PENDING_DOCUMENTS.DOCUMENTS.${DocumentType[pendingDocument.type]}`
    );
  };

  return (
    <div className="pending-documents">
      <div
        className="pending-documents__sidebar"
        style={{ background: currentTheme.background }}
      />
      <div className="pending-documents__list">
        <AdvancedPanelContentHeader
          title={translate('PAGES.PENDING_DOCUMENTS.TITLE')}
          description={translate('PAGES.PENDING_DOCUMENTS.SUBTITLE')}
          testId="pending-documents-header"
          goBackAction={handleLogout}
          buttonTitle={translate('PAGES.PENDING_DOCUMENTS.LOGOUT')}
        />
        <AdvancedCard testId="pending-documents-card">
          {pendingDocuments
            .sort((a, b) => {
              if (a.type === DocumentType.ID_WALL_SELFIE_DOCS) return 1;
              if (b.type === DocumentType.ID_WALL_SELFIE_DOCS) return -1;
              return 0;
            })
            .map((pendingDocument) => {
              const isIdWallSelfie =
                pendingDocument.type === DocumentType.ID_WALL_SELFIE_DOCS;
              const hasOtherPendingDocuments =
                isIdWallSelfie &&
                pendingDocuments.some(
                  (doc) =>
                    doc.id !== pendingDocument.id &&
                    !doc.isOK &&
                    doc.status !== DocumentPendingStatus.NO_PENDING
                );

              const isDisabled = hasOtherPendingDocuments;
              const isDone =
                pendingDocument.isOK ||
                pendingDocument.status === DocumentPendingStatus.NO_PENDING;

              return (
                <div
                  key={pendingDocument.id}
                  className={`pending-documents__card-item ${
                    isDisabled ? 'disabled' : ''
                  }`}
                  onClick={() => !isDisabled && handlePress(pendingDocument)}
                  title={
                    isDisabled
                      ? translate(
                          'PAGES.PENDING_DOCUMENTS.ID_WALL_MUST_BE_LAST'
                        )
                      : ''
                  }
                >
                  <span className="pending-documents__card-item__name">
                    {handleDocumentName(pendingDocument)}
                  </span>
                  <span className="pending-documents__card-item__icon">
                    {isDone ? <CheckCircleOutlined /> : <RightOutlined />}
                  </span>
                </div>
              );
            })}
        </AdvancedCard>
      </div>
      {renderModal()}
    </div>
  );
};

export default PendingDocuments;
